import { FC, lazy, Suspense, useMemo, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { LoadingScreen, MappingModal } from '@/components';
import { TutorialRunner } from '@/components/TutorialRunner';
import { TUTORIAL_ANCHORS } from '@/constants';
import { useIsAdminRole, useMappingModalContext, useTutorial } from '@/hooks';
import { airesMappingStatusQuery } from '@/query';
import { APP_ROUTES } from '@/routes';
import { getUploadPageConfig } from '@/utils';

import styles from './styles.module.scss';

const AiresFiles = lazy(() => import('@/pages/AiresFiles'));
const LoansMapping = lazy(() => import('@/pages/LoansMapping'));
const SharesMapping = lazy(() => import('@/pages/SharesMapping'));
const CodeMapping = lazy(() => import('@/pages/CodeMapping'));
const MappingUpload = lazy(() => import('@/pages/MappingUpload'));
const UploadHistory = lazy(() => import('@/pages/UploadHistory'));

export const TabsLayout: FC = () => {
  const { isAdmin } = useIsAdminRole();
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const { shouldShowModal, hideModalTemporarily, hideModalPermanently } = useMappingModalContext();
  const { isActive: isTutorialActive } = useTutorial();

  const { data } = useQuery(airesMappingStatusQuery());

  const { mappingCounts, showMapping } = useMemo(() => {
    const result = data?.result;

    if (!result) {
      return {
        mappingCounts: null,
        showMapping: false,
      };
    }

    return {
      mappingCounts: data?.result,
      showMapping: result.loanCodesUnmappedCount + result.shareCodesUnmappedCount + result.pfcCodesUnmappedCount > 0,
    };
  }, [data?.result]);

  const { isUploadPage, uploadPageConfig } = useMemo(() => getUploadPageConfig(location.pathname), [location]);

  const TAB_CONFIG = useMemo(
    () => [
      {
        label: 'AIRES files',
        route: APP_ROUTES.aires,
        component: AiresFiles,
        count: 0,
        tabAnchor: TUTORIAL_ANCHORS.airesTab,
        counterAnchor: undefined,
      },
      {
        label: 'Loans Mapping',
        route: APP_ROUTES.loansMapping,
        component: LoansMapping,
        count: mappingCounts?.loanCodesUnmappedCount ?? 0,
        tabAnchor: TUTORIAL_ANCHORS.loansTab,
        counterAnchor: TUTORIAL_ANCHORS.loansUnmappedCounter,
      },
      {
        label: 'Shares Mapping',
        route: APP_ROUTES.sharesMapping,
        component: SharesMapping,
        count: mappingCounts?.shareCodesUnmappedCount ?? 0,
        tabAnchor: TUTORIAL_ANCHORS.sharesTab,
        counterAnchor: TUTORIAL_ANCHORS.sharesUnmappedCounter,
      },
      {
        label: 'Code Mapping',
        route: APP_ROUTES.codeMapping,
        component: CodeMapping,
        count: mappingCounts?.pfcCodesUnmappedCount ?? 0,
        tabAnchor: TUTORIAL_ANCHORS.pfcTab,
        counterAnchor: TUTORIAL_ANCHORS.pfcUnmappedCounter,
      },
      {
        label: 'Upload History',
        route: APP_ROUTES.uploadHistory,
        component: UploadHistory,
        count: 0,
        isVisible: !isAdmin,
        tabAnchor: undefined,
        counterAnchor: undefined,
      },
    ],
    [mappingCounts, isAdmin],
  );

  const CurrentTab = useMemo(
    () => TAB_CONFIG.find((tab) => location.pathname === tab.route),
    [TAB_CONFIG, location.pathname],
  );

  const handleNavigation = (route: string) => {
    navigate(route);
  };

  const isVisibleMappingModal =
    location.pathname === APP_ROUTES.aires && showMapping && shouldShowModal && !!mappingCounts && !isTutorialActive;

  const handleMappingModalClose = () => {
    hideModalPermanently();
  };

  return (
    <>
      <div className={styles.tabWrapper}>
        <nav className={styles.tabs}>
          {TAB_CONFIG.map((tab) => {
            if (!!tab?.isVisible) return null;

            const isCurrentTab = location.pathname.includes(tab.route);

            return (
              <button
                role="button"
                className={clsx(styles.tab, isCurrentTab && styles.tabActive)}
                key={tab.route}
                onClick={() => handleNavigation(tab.route)}
                disabled={isCurrentTab}
                data-tour={tab.tabAnchor}
              >
                {tab.label}
                {tab.count > 0 && (
                  <div className={styles.tabIcon} data-tour={tab.counterAnchor}>
                    {tab.count}
                  </div>
                )}
              </button>
            );
          })}

          <div className={styles.tabsLine} role="presentation"></div>
        </nav>

        <SwitchTransition mode="out-in">
          <CSSTransition
            key={location.pathname}
            classNames="slide-right"
            timeout={300}
            nodeRef={contentRef}
            unmountOnExit
          >
            <main ref={contentRef} className={styles.tabPanel}>
              <Suspense fallback={<LoadingScreen />}>
                {isUploadPage && (
                  <MappingUpload
                    mappingType={uploadPageConfig.mappingType}
                    downloadTemplateLink={uploadPageConfig.downloadTemplateLink}
                  />
                )}

                {!isUploadPage && CurrentTab && <CurrentTab.component />}
              </Suspense>
            </main>
          </CSSTransition>
        </SwitchTransition>
      </div>

      {isVisibleMappingModal && (
        <MappingModal
          isOpen
          modalData={mappingCounts}
          onClose={handleMappingModalClose}
          onOverlayClick={hideModalTemporarily}
        />
      )}

      <TutorialRunner />
    </>
  );
};
